// FixturesSync — feed the real leagues, seasons and fixtures into the legacy
// selectors (WS-C; BenBet W1 PR 3b).
//
// The global Country / League / Season / Fixture `<select>`s + their custom
// picker are still legacy DOM owned by main.js. But the app-api base URL lives
// only in the React bundle (`import.meta.env.VITE_APP_API_URL`), so main.js can't
// fetch itself. This component bridges the gap: React fetches, the store holds
// the selection, and `window.bentoShell` swaps the `<option>`s in place (the
// picker reads options live, so no re-init).
//
// LOAD ORDER (why two independent queries): the store starts on the default
// league-season (PL 26/27), so its fixture list loads IN PARALLEL with
// GET /competitions — widgets don't wait for the dropdown data first.
//
// Only the next two rounds are offered, in date order (CLAUDE.md "What Bento is
// for"): Bento is a pre-match tool — played matches have nothing left to bet on,
// and later rounds have no markets yet. Default fixture: on first load, the next
// upcoming match. On a later league switch, the same pairing is kept when the new
// list has it, else the next upcoming.
//
// Renders nothing. Mounted once at the app root. If GET /fixtures fails, the store's
// `fixturesError` makes widgets show an error rather than spin forever.

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { CompetitionSeason, Fixture } from "@bento/shared";
import { apiGet } from "./api.js";
import { useBentoStore } from "../store/index.js";

const when = (f: Fixture) => `${f.date ?? ""} ${f.kickOff ?? ""}`;

/** How many rounds of upcoming matches the fixture selector offers. */
export const UPCOMING_ROUNDS = 2;

/** The season's unplayed matches, in date order, cut to the next `rounds` rounds
 *  (a round = every team plays once = teams ÷ 2 matches: 10 in the PL, 12 in the
 *  Championship). The data has no round numbers, so a round is counted in matches.
 *  A season with nothing left to play (finished) falls back to its full list. */
export function upcomingFixtures(fixtures: readonly Fixture[], rounds = UPCOMING_ROUNDS): Fixture[] {
  const teams = new Set(fixtures.flatMap((f) => [f.home, f.away]));
  const perRound = Math.max(1, Math.floor(teams.size / 2));
  const upcoming = fixtures
    .filter((f) => !f.played)
    .sort((a, b) => when(a).localeCompare(when(b)))
    .slice(0, perRound * rounds);
  return upcoming.length ? upcoming : [...fixtures];
}

/** The season's next match to be played (earliest unplayed), else its first. */
export function defaultFixtureLabel(fixtures: readonly Fixture[]): string | undefined {
  return upcomingFixtures(fixtures, 1)[0]?.label;
}

export default function FixturesSync() {
  const competitionId = useBentoStore((s) => s.competitionId);
  const seasonId = useBentoStore((s) => s.seasonId);
  const competitions = useBentoStore((s) => s.competitions);
  const firstLoad = useRef(true);

  const comps = useQuery({
    queryKey: ["competitions"],
    queryFn: () => apiGet<{ competitions: CompetitionSeason[] }>("/competitions"),
    staleTime: Infinity, // config-backed — changes only with a deploy
  });

  const fixtures = useQuery({
    queryKey: ["fixtures", competitionId, seasonId],
    queryFn: () =>
      apiGet<{ fixtures: Fixture[] }>(`/fixtures?competition=${competitionId}&season=${seasonId}`),
    // A live season's list changes daily (results, referees); app-api refreshes
    // its own copy hourly, so re-asking more often than that gains nothing.
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (comps.data?.competitions?.length) {
      useBentoStore.getState().setCompetitions(comps.data.competitions);
    }
  }, [comps.data]);

  // Re-fill the country / league / season selects when the list or selection moves.
  useEffect(() => {
    if (competitions.length) {
      window.bentoShell?.populateCompetitions?.(competitions, competitionId, seasonId);
    }
  }, [competitions, competitionId, seasonId]);

  useEffect(() => {
    useBentoStore.getState().setFixturesError(fixtures.isError);
  }, [fixtures.isError]);

  useEffect(() => {
    const list = fixtures.data?.fixtures;
    if (!list?.length) return;
    // Label → match id first, so widgets (useFixtureKey) can fetch as soon as the
    // selector shows the list (BenBet W1).
    useBentoStore.getState().setFixtureIds(list);
    const shown = upcomingFixtures(list);
    window.bentoShell?.populateFixtures?.(shown, shown[0]?.label, firstLoad.current);
    firstLoad.current = false;
  }, [fixtures.data]);

  return null;
}
